// src/components/requests/RequestForm.jsx
import React, { useCallback, useMemo, useState } from 'react';

export const AppView = {
  HOME: 'HOME',
  RESULTS: 'RESULTS',
  PROVIDER: 'PROVIDER',
  USER_DASHBOARD: 'USER_DASHBOARD',
};

const inferVerticalFromDescription = (text = '') => {
  const value = String(text).toLowerCase();
  if (/hotel|accommodation|stay|guest house|airbnb|lodge|check-in|check in|check-out|check out/.test(value)) return 'accommodation';
  if (/wedding|event|catering|decor|photography|venue|dj/.test(value)) return 'event_services';
  if (/tutor|lesson|training|course|teach|coaching|student/.test(value)) return 'tutoring_training';
  if (/delivery|courier|transport|logistics|pickup|dropoff|truck|moving/.test(value)) return 'logistics_delivery';
  if (/plumber|electrician|repair|cleaning|renovation|paint|leak|home/.test(value)) return 'home_services';
  return 'professional_services';
};

const inferCategoryFromVertical = (vertical) => {
  switch (vertical) {
    case 'accommodation':
      return 'Accommodation';
    case 'event_services':
      return 'Event Services';
    case 'tutoring_training':
      return 'Tutoring & Training';
    case 'logistics_delivery':
      return 'Logistics & Delivery';
    case 'home_services':
      return 'Home Services';
    default:
      return 'Professional Services';
  }
};

const splitList = (value = '') =>
  String(value || '')
    .split(/[\n,]+/)
    .map((item) => item.trim())
    .filter(Boolean);

const extractBudget = (text = '') => {
  const match = String(text).match(/(?:r|zar|\$|usd|eur|gbp)?\s?(\d[\d\s,]*)/i);
  if (!match) return null;
  const value = Number(String(match[1]).replace(/[,\s]/g, ''));
  return Number.isFinite(value) ? value : null;
};

const extractLocation = (text = '') => {
  const match = String(text).match(/\b(?:in|at|near)\s+([A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+){0,3})/);
  return match ? match[1].trim() : '';
};

const buildClarifyingQuestions = (text = '', vertical = 'professional_services') => {
  const lower = String(text).toLowerCase();
  const questions = [];

  if (!extractLocation(text)) {
    questions.push({ id: 'location', label: 'Where should this happen?', placeholder: 'Cape Town CBD, online, Sandton, etc.' });
  }

  if (!/\b(today|tomorrow|week|month|july|august|september|october|november|december|january|february|march|april|may|june|\d{4}-\d{2}-\d{2})\b/i.test(lower)) {
    questions.push({ id: 'timeframe', label: 'When do you need this?', placeholder: 'Tomorrow morning, July 12-15, next week, etc.' });
  }

  if (!extractBudget(text)) {
    questions.push({ id: 'budget', label: 'What budget range should the agent use?', placeholder: 'Around R4,000 total, under R800 per session, etc.' });
  }

  if (vertical === 'accommodation' && !/\bguest|people|person|adult|adults|room|rooms\b/.test(lower)) {
    questions.push({ id: 'capacity', label: 'How many guests or rooms are needed?', placeholder: '2 adults, 1 child, 2 rooms, etc.' });
  }

  if (vertical === 'event_services' && !/\bguest|people|attendees|service|catering|decor|photo|music\b/.test(lower)) {
    questions.push({ id: 'services', label: 'What services are needed?', placeholder: 'Catering + decor for 80 guests, venue + DJ, etc.' });
  }

  if (vertical === 'tutoring_training' && !/\bsubject|math|science|english|grade|level|exam\b/.test(lower)) {
    questions.push({ id: 'learning_scope', label: 'What subject or learning outcome is needed?', placeholder: 'Grade 11 maths, beginner Excel, IELTS prep, etc.' });
  }

  return questions.slice(0, 3);
};

const RequestForm = ({
  onSubmit,
  isSubmitting,
  isDarkMode,
  bookData,
  placeholder,
  user,
  onRequireAuth
}) => {
  const [description, setDescription] = useState('');
  const [showIntentConfirm, setShowIntentConfirm] = useState(false);
  const [intentSummary, setIntentSummary] = useState('');
  const [mustHaves, setMustHaves] = useState('');
  const [dealBreakers, setDealBreakers] = useState('');
  const [clarifyingAnswers, setClarifyingAnswers] = useState({});

  const primaryColor = bookData?.styling?.primaryColor || '#6366f1';
  const accentColor = bookData?.styling?.accentColor || '#8b5cf6';
  const inferredVertical = useMemo(() => inferVerticalFromDescription(description), [description]);
  const clarifyingQuestions = useMemo(
    () => buildClarifyingQuestions(description, inferredVertical),
    [description, inferredVertical]
  );

  const ensureAuth = useCallback(() => {
    if (user?.uid) return true;
    if (onRequireAuth) onRequireAuth();
    return false;
  }, [onRequireAuth, user]);

  const buildMissionPayload = useCallback((summary) => {
    const category = inferCategoryFromVertical(inferredVertical);
    const combinedDescription = [
      description.trim(),
      ...clarifyingQuestions.map((question) => {
        const answer = clarifyingAnswers[question.id];
        return answer ? `${question.label} ${answer}` : '';
      }).filter(Boolean)
    ].join('\n');

    const locationAnswer = clarifyingAnswers.location || extractLocation(description);
    const timeframeAnswer = clarifyingAnswers.timeframe || '';
    const budgetText = clarifyingAnswers.budget || '';
    const budgetDetected = extractBudget(description) || extractBudget(budgetText);

    const verticalData = {
      clarificationAnswers: clarifyingAnswers
    };

    return {
      title: summary,
      description: combinedDescription,
      category,
      vertical: inferredVertical,
      location: locationAnswer,
      timeframe: timeframeAnswer || 'flexible',
      budget: budgetDetected,
      budgetMin: budgetDetected,
      budgetMax: budgetDetected,
      maxResponses: bookData?.requestsSettings?.defaultMaxResponses || 3,
      demandProfile: {
        summary,
        category,
        location: locationAnswer,
        timeframe: timeframeAnswer || 'flexible',
        budgetMin: budgetDetected,
        budgetMax: budgetDetected,
        mustHaves: splitList(mustHaves),
        dealBreakers: splitList(dealBreakers),
        preferences: [],
        constraints: [],
        normalizedLocation: locationAnswer,
        requestedDates: {
          startDate: '',
          endDate: ''
        },
        requestedCapacity: clarifyingAnswers.capacity || '',
        requestedUnits: '',
        serviceWindow: timeframeAnswer || ''
      },
      intentConfirmation: {
        summary,
        mustHaves: splitList(mustHaves),
        dealBreakers: splitList(dealBreakers),
        preferences: [],
        constraints: []
      },
      verticalData
    };
  }, [bookData, clarifyingAnswers, clarifyingQuestions, description, inferredVertical, mustHaves, dealBreakers]);

  const startIntentConfirmation = useCallback((e) => {
    e.preventDefault();
    if (!ensureAuth() || !description.trim()) return;
    setIntentSummary(description.trim());
    setShowIntentConfirm(true);
  }, [description, ensureAuth]);

  const submitMission = useCallback((summary) => {
    if (!ensureAuth() || !description.trim() || !summary.trim()) return;
    onSubmit(buildMissionPayload(summary.trim()));
  }, [buildMissionPayload, description, ensureAuth, onSubmit]);

  const submitConfirmedIntent = useCallback(() => {
    submitMission(intentSummary);
  }, [intentSummary, submitMission]);

  const submitDirectRequest = useCallback(() => {
    submitMission(description.trim());
  }, [description, submitMission]);

  return (
    <div className="w-full max-w-4xl mx-auto animate-fade-in-up flex flex-col items-center">
      {!bookData?.title && (
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-slate-900 dark:text-white mb-4">
            Stop searching. <span
              className="text-transparent bg-clip-text bg-gradient-to-r"
              style={{ backgroundImage: `linear-gradient(to right, ${primaryColor}, ${accentColor})` }}
            >
              State the outcome
            </span>
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Describe what you need in plain language. Agent King infers the mission type and only asks follow-up questions when key details are missing.
          </p>
        </div>
      )}

      <div className="w-full max-w-3xl">
        <form onSubmit={startIntentConfirmation}>
          <div
            className={`rounded-2xl shadow-2xl border transition-colors duration-300 ${
              isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white'
            }`}
            style={!isDarkMode ? { border: primaryColor } : {}}
          >
            <div className="p-3 sm:p-4">
              <label className={`block text-xs font-semibold uppercase tracking-wider mb-2 ${
                isDarkMode ? 'text-slate-400' : 'text-slate-500'
              }`}>
                What outcome do you want?
              </label>
              <textarea
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  setShowIntentConfirm(false);
                }}
                placeholder={placeholder || 'E.g., I need a place to stay in July in Cape Town CBD within a specified budget.'}
                rows={4}
                className={`w-full px-0 py-2 border-none focus:ring-0 outline-none resize-none text-md placeholder-slate-400 transition-colors ${
                  isDarkMode ? 'bg-transparent text-white placeholder-slate-500' : 'bg-transparent text-slate-900 placeholder-slate-400'
                }`}
                required
              />
            </div>

            <div className={`border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}></div>

            <div className="p-6 sm:p-8">
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${
                  isDarkMode ? 'bg-slate-700 text-slate-200' : 'bg-slate-100 text-slate-700'
                }`}>
                  Inferred vertical: {inferredVertical.replace(/_/g, ' ')}
                </span>
                <span className={`text-xs ${
                  isDarkMode ? 'text-slate-400' : 'text-slate-500'
                }`}>
                  Agent King will clarify only what is missing.
                </span>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={submitDirectRequest}
                  disabled={isSubmitting || !description.trim()}
                  className={`px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${
                    isSubmitting || !description.trim()
                      ? isDarkMode
                        ? 'bg-slate-700 text-slate-400 cursor-not-allowed opacity-50'
                        : 'bg-slate-300 text-slate-500 cursor-not-allowed opacity-50'
                      : isDarkMode
                        ? 'bg-slate-700 text-white hover:bg-slate-600'
                        : 'bg-slate-100 text-slate-800 hover:bg-slate-200'
                  }`}
                >
                  Launch Mission
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !description.trim()}
                  className={`px-8 py-3 rounded-xl font-semibold text-white transition-all duration-200 flex items-center gap-2 ${
                    isSubmitting || !description.trim()
                      ? isDarkMode
                        ? 'bg-slate-700 cursor-not-allowed opacity-50'
                        : 'bg-slate-300 cursor-not-allowed opacity-50'
                      : 'shadow-lg hover:shadow-xl transform hover:scale-[1.02]'
                  }`}
                  style={!(isSubmitting || !description.trim()) ? {
                    backgroundColor: primaryColor,
                    boxShadow: `0 10px 25px -5px ${primaryColor}40`
                  } : {}}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Launching agents...</span>
                    </>
                  ) : (
                    <>
                      <span>Clarify with Agent</span>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {showIntentConfirm && (
            <div className={`mt-4 rounded-2xl border p-5 ${
              isDarkMode ? 'border-slate-700 bg-slate-800/60' : 'border-slate-200 bg-white'
            }`}>
              <div className={`mb-4 text-sm font-semibold uppercase tracking-wide ${
                isDarkMode ? 'text-emerald-300' : 'text-emerald-700'
              }`}>
                Agent King Clarifying Questions
              </div>

              <div className={`mb-4 rounded-xl p-3 text-sm ${
                isDarkMode ? 'bg-slate-900/60 text-slate-200' : 'bg-slate-50 text-slate-700'
              }`}>
                Agent King inferred <strong>{inferredVertical.replace(/_/g, ' ')}</strong>. Answer only the missing details below, then launch matching.
              </div>

              <div className="space-y-3">
                <Field
                  label="Confirm your goal in one sentence"
                  value={intentSummary}
                  onChange={setIntentSummary}
                  isDarkMode={isDarkMode}
                  primaryColor={primaryColor}
                />

                {clarifyingQuestions.map((question) => (
                  <Field
                    key={question.id}
                    label={question.label}
                    value={clarifyingAnswers[question.id] || ''}
                    onChange={(value) => setClarifyingAnswers((prev) => ({ ...prev, [question.id]: value }))}
                    isDarkMode={isDarkMode}
                    primaryColor={primaryColor}
                    placeholder={question.placeholder}
                  />
                ))}

                <Field
                  label="Must-haves"
                  value={mustHaves}
                  onChange={setMustHaves}
                  isDarkMode={isDarkMode}
                  primaryColor={primaryColor}
                  placeholder="verified provider, central location, breakfast included"
                />

                <Field
                  label="Deal-breakers"
                  value={dealBreakers}
                  onChange={setDealBreakers}
                  isDarkMode={isDarkMode}
                  primaryColor={primaryColor}
                  placeholder="shared bathroom, remote-only, no weekend availability"
                />
              </div>

              <div className="mt-4 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowIntentConfirm(false)}
                  className={`rounded-xl px-4 py-2 text-sm font-semibold ${
                    isDarkMode ? 'border border-slate-600 bg-slate-800 text-slate-200' : 'border border-slate-300 bg-white text-slate-700'
                  }`}
                >
                  Edit Mission
                </button>
                <button
                  type="button"
                  onClick={submitConfirmedIntent}
                  disabled={isSubmitting || !intentSummary.trim()}
                  className={`rounded-xl px-5 py-2 text-sm font-semibold text-white ${
                    isSubmitting || !intentSummary.trim() ? 'cursor-not-allowed bg-slate-400 opacity-60' : ''
                  }`}
                  style={!(isSubmitting || !intentSummary.trim()) ? { backgroundColor: primaryColor } : {}}
                >
                  Confirm and Launch
                </button>
              </div>
            </div>
          )}
        </form>

        <div className="mt-6 flex flex-col items-center">
          <div className={`flex items-center gap-2 text-sm ${
            isDarkMode ? 'text-slate-400' : 'text-slate-500'
          }`}>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            <span>Sign-in required. Describe the need naturally and let Agent King infer the mission shape.</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({
  label,
  value,
  onChange,
  isDarkMode = false,
  primaryColor = '#6366f1',
  placeholder = ''
}) => (
  <div className="relative">
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full px-4 py-3 rounded-xl border focus:ring-2 focus:border-transparent outline-none transition-all text-sm ${
        isDarkMode
          ? 'bg-slate-900/50 border-slate-700 text-white placeholder-slate-500 focus:ring-indigo-500'
          : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400 focus:ring-indigo-500'
      }`}
      style={{ '--tw-ring-color': primaryColor }}
    />
    <label className={`absolute -top-2 left-2 px-1 text-[10px] font-bold uppercase tracking-wider ${
      isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-white text-slate-400'
    }`}>
      {label}
    </label>
  </div>
);

export default RequestForm;
